import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Plus, Trash2, Coins } from 'lucide-react';

const EMPTY_FORM = {
  campaign_id: '',
  amount: '',
  currency: 'USD',
  period_start: '',
  period_end: '',
  note: '',
};

const fmtMoney = (amount, currency) => {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
    }).format(n);
  } catch (e) {
    // Unknown/invalid currency code — fall back to a plain number + code.
    return `${n.toFixed(2)} ${(currency || 'USD').toUpperCase()}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function CostEntries() {
  const { t } = useT();
  const [entries, setEntries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [entriesRes, campaignsRes] = await Promise.all([
        axios.get(API + '/cost-entries'),
        axios.get(API + '/campaigns'),
      ]);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : []);
    } catch (err) {
      console.error('Error loading cost entries:', err);
      toast.error(t('Error loading cost entries'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const campaignName = (id) => {
    const c = campaigns.find((x) => x.id === id);
    return c ? c.name : id;
  };

  const handleCreate = async () => {
    if (!form.campaign_id) {
      toast.error(t('Select a campaign'));
      return;
    }
    const amount = Number(form.amount);
    if (form.amount === '' || Number.isNaN(amount) || !Number.isFinite(amount)) {
      toast.error(t('Enter a valid amount'));
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(API + '/cost-entries', {
        campaign_id: form.campaign_id,
        amount,
        currency: (form.currency || 'USD').toUpperCase(),
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        note: form.note ? form.note.trim() : null,
      });
      setEntries((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Cost entry added'));
    } catch (err) {
      console.error('Error creating cost entry:', err);
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(detail || t('Error creating cost entry'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await axios.delete(API + '/cost-entries/' + id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setDeleteTarget(null);
      toast.success(t('Cost entry deleted'));
    } catch (err) {
      console.error('Error deleting cost entry:', err);
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(detail || t('Error deleting cost entry'));
    }
  };

  const renderForm = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>{t('Campaign')}</Label>
        <Select
          value={form.campaign_id}
          onValueChange={(v) => setForm((p) => ({ ...p, campaign_id: v }))}
        >
          <SelectTrigger><SelectValue placeholder={t('Select a campaign')} /></SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Amount')}</Label>
          <Input
            type="number"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground">{t('Use a negative amount to book a correction.')}</p>
        </div>
        <div className="space-y-2">
          <Label>{t('Currency')}</Label>
          <Input
            value={form.currency}
            maxLength={3}
            onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
            placeholder="USD"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Period start')}</Label>
          <Input type="date" value={form.period_start} onChange={(e) => setForm((p) => ({ ...p, period_start: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t('Period end')}</Label>
          <Input type="date" value={form.period_end} onChange={(e) => setForm((p) => ({ ...p, period_end: e.target.value }))} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t('Leave the period blank to record spend as of now.')}</p>
      <div className="space-y-2">
        <Label>{t('Note')}</Label>
        <Textarea
          value={form.note}
          rows={2}
          onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
          placeholder={t('e.g. Q3 influencer flat fee')}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Workspace')}
        title={t('Cost entries')}
        subtitle={t('Manually record campaign spend the ad-account sync can’t see — flat fees, offline media, corrections.')}
        actions={
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}><Plus className="w-4 h-4 mr-2" />{t('Add cost')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('Add manual cost')}</DialogTitle>
                <DialogDescription>{t('Book spend against a campaign. It is folded into the campaign cost used by ROI and dashboards.')}</DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('Cancel')}</Button>
                <Button onClick={handleCreate} disabled={saving}>{saving ? t('Saving…') : t('Save')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={Coins}
              title={t('No cost entries yet')}
              subtitle={t('Add a manual cost, or connect an ad account to sync spend automatically.')}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Campaign')}</TableHead>
                  <TableHead>{t('Amount')}</TableHead>
                  <TableHead>{t('Source')}</TableHead>
                  <TableHead>{t('Period')}</TableHead>
                  <TableHead>{t('Note')}</TableHead>
                  <TableHead className="text-right">{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => {
                  const manual = e.provider === 'manual';
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{campaignName(e.campaign_id)}</TableCell>
                      <TableCell className={Number(e.amount) < 0 ? 'text-emerald-600' : ''}>
                        {fmtMoney(e.amount, e.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={manual ? 'secondary' : 'outline'} className="text-xs capitalize">
                          {manual ? t('Manual') : e.provider}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {fmtDate(e.period_start)} → {fmtDate(e.period_end)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[16rem] truncate">
                        {e.note || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {manual ? (
                          <Button variant="ghost" size="sm" aria-label={t('Delete cost entry')} title={t('Delete cost entry')} onClick={() => setDeleteTarget(e)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('Synced')}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete cost entry')}
        description={t('This removes the manual cost and reverses its contribution to the campaign spend.')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
